<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { X, ExternalLink } from 'lucide-vue-next'

/**
 * 2026-09-14 사용자 지시 — "모든 상황이 인앱브라우저처럼 우리 meet에서 움직이도록" 대응.
 * §19 "Room 진입 후 페이지 이동 금지" 원칙을 외부 링크에도 적용 — 프로필/녹화 다시보기/자료
 * 원본 같은 외부 URL을 새 탭으로 튕기지 않고 iframe으로 감싸 Room 위에 그대로 띄운다(카카오톡
 * 인앱 웹뷰 탈출 스크립트와는 반대 방향 — 여기선 "밖으로 안 나가게" 붙잡아두는 쪽).
 *
 * 일부 사이트는 iframe 삽입 자체를 X-Frame-Options/CSP로 막는다 — 그런 경우 iframe이
 * 비어보이므로 "새 탭에서 열기" 탈출구를 항상 같이 보여준다.
 */
const props = defineProps<{ url: string; title?: string }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
</script>

<template>
  <div class="in-app-browser">
    <header class="in-app-browser__bar">
      <span class="in-app-browser__title">{{ props.title || props.url }}</span>
      <a class="in-app-browser__external" :href="props.url" target="_blank" rel="noopener" :title="t('inAppBrowser.openExternally')">
        <ExternalLink :size="16" />
      </a>
      <button class="in-app-browser__close" :aria-label="t('common.close')" @click="emit('close')">
        <X :size="18" />
      </button>
    </header>
    <iframe class="in-app-browser__frame" :src="props.url" referrerpolicy="no-referrer" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
  </div>
</template>

<style scoped>
.in-app-browser {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  background: var(--meet-surface-solid);
}

@media (prefers-color-scheme: dark) {
  .in-app-browser {
    background: var(--meet-surface-solid-dark);
  }
}

.in-app-browser__bar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  padding-top: calc(var(--space-3) + env(safe-area-inset-top));
  border-bottom: 1px solid var(--meet-border);
  flex-shrink: 0;
}

.in-app-browser__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: var(--meet-text-secondary);
}

.in-app-browser__external,
.in-app-browser__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--meet-text);
  flex-shrink: 0;
}

.in-app-browser__external:hover,
.in-app-browser__close:hover {
  background: rgba(0, 0, 0, 0.06);
}

.in-app-browser__frame {
  flex: 1;
  min-height: 0;
  width: 100%;
  border: none;
  background: #fff;
}
</style>
